import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { safeFormat } from "@/lib/dateUtils";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StarRating } from "@/components/StarRating";
import { Loader2, MessageSquare, Eye, EyeOff, TrendingUp, Star, Users, Clock, Phone, RefreshCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/useMobile";

export default function AdminReviews() {
  const utils = trpc.useUtils();
  const isMobile = useIsMobile();
  const [replyTarget, setReplyTarget] = useState<{ id: number; currentReply?: string | null } | null>(null);
  const [replyText, setReplyText] = useState("");
  const [activeTab, setActiveTab] = useState("received");

  const { data: mySalon } = trpc.salon.getMySalon.useQuery();
  const salonId = mySalon?.id;

  const statsInput = useMemo(() => ({ salonId: salonId! }), [salonId]);
  const profsInput = useMemo(() => ({ salonId: salonId! }), [salonId]);
  const servicesInput = useMemo(() => ({ salonId: salonId! }), [salonId]);

  const { data: reviews, isLoading } = trpc.reviews.listAdmin.useQuery();
  const { data: pendingSessions, isLoading: isPendingLoading, refetch: refetchPending } = trpc.reviews.listPending.useQuery();
  const { data: stats } = trpc.reviews.getStats.useQuery(statsInput, { enabled: !!salonId });
  const { data: professionals } = trpc.professionals.list.useQuery(profsInput, { enabled: !!salonId });
  const { data: services } = trpc.services.list.useQuery(servicesInput, { enabled: !!salonId });

  const replyMutation = trpc.reviews.reply.useMutation({
    onSuccess: () => {
      toast.success("Resposta enviada com sucesso!");
      utils.reviews.listAdmin.invalidate();
      utils.reviews.listPublic.invalidate();
      setReplyTarget(null);
      setReplyText("");
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const toggleMutation = trpc.reviews.togglePublish.useMutation({
    onSuccess: (_, vars) => {
      toast.success(vars.isPublished ? "Avaliação publicada." : "Avaliação ocultada.");
      utils.reviews.listAdmin.invalidate();
      utils.reviews.listPublic.invalidate();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const getProfName = (id: number) => professionals?.find((p) => p.id === id)?.name ?? "—";
  const getServiceName = (id: number) => (services as Array<{id: number; name: string}> | undefined)?.find((s) => s.id === id)?.name ?? "—";

  const avgRating = stats?.averageRating ? Number(stats.averageRating).toFixed(1) : "—";
  const publishedReviews = reviews?.filter((review) => review.isPublished).length ?? 0;
  const totalReviews = publishedReviews;
  const pendingCount = pendingSessions?.length ?? 0;

  return (
    <div className={isMobile ? "p-3 space-y-4" : "p-6 space-y-6"}>
      <div>
        <h1 className={isMobile ? "text-2xl font-[Cormorant_Garamond,serif] font-semibold text-foreground" : "text-2xl font-[Cormorant_Garamond,serif] font-semibold text-foreground"}>
          Avaliações
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Notas dos atendimentos concluídos. A pesquisa sobre a IA é acompanhada separadamente na Central Omnichannel.
        </p>
      </div>

      {/* Stats */}
      <div className={isMobile ? "grid grid-cols-2 gap-3" : "grid grid-cols-1 sm:grid-cols-4 gap-4"}>
        <Card className="border-border/50">
          <CardContent className={`${isMobile ? "p-3" : "p-4"} flex items-center gap-3`}>
            <div className={`${isMobile ? "w-11 h-11" : "w-10 h-10"} rounded-full bg-amber-100 flex items-center justify-center shrink-0`}>
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{avgRating}</p>
              <p className="text-xs text-muted-foreground">Nota média</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className={`${isMobile ? "p-3" : "p-4"} flex items-center gap-3`}>
            <div className={`${isMobile ? "w-11 h-11" : "w-10 h-10"} rounded-full bg-blue-100 flex items-center justify-center shrink-0`}>
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{totalReviews}</p>
              <p className="text-xs text-muted-foreground">Recebidas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className={`${isMobile ? "p-3" : "p-4"} flex items-center gap-3`}>
            <div className={`${isMobile ? "w-11 h-11" : "w-10 h-10"} rounded-full bg-green-100 flex items-center justify-center shrink-0`}>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{publishedReviews}</p>
              <p className="text-xs text-muted-foreground">Publicadas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className={`${isMobile ? "p-3" : "p-4"} flex items-center gap-3`}>
            <div className={`${isMobile ? "w-11 h-11" : "w-10 h-10"} rounded-full bg-orange-100 flex items-center justify-center shrink-0`}>
              <Clock className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{pendingCount}</p>
              <p className="text-xs text-muted-foreground">Aguardando resposta</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs: Avaliações recebidas + Pendentes */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {isMobile ? (
        <div className="mb-2">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecione a seção" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="received">Avaliações Recebidas</SelectItem>
              <SelectItem value="pending">Aguardando Resposta</SelectItem>
            </SelectContent>
          </Select>
        </div>
        ) : (
        <TabsList className="inline-flex mb-4">
          <TabsTrigger value="received">
            Avaliações Recebidas
            {totalReviews > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">{totalReviews}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="pending">
            Aguardando Resposta
            {pendingCount > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs bg-orange-100 text-orange-700">{pendingCount}</Badge>
            )}
          </TabsTrigger>
        </TabsList>
        )}

        {/* Tab: Avaliações recebidas */}
        <TabsContent value="received">
          <Card className="border-border/50">
            <CardHeader className={isMobile ? "px-4 pt-4 pb-2" : "pb-3"}>
              <CardTitle className="text-base font-semibold">Todas as Avaliações</CardTitle>
            </CardHeader>
            <CardContent className={isMobile ? "px-3 pb-3" : undefined}>
              {isLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : !reviews || reviews.length === 0 ? (
                <div className="text-center py-10">
                  <Star className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">Nenhuma avaliação ainda.</p>
                </div>
              ) : (
                <div className={isMobile ? "space-y-3" : "space-y-4"}>
                  {reviews.map((review) => (
                    <div
                      key={review.id}
                      className={`${isMobile ? "p-3" : "p-4"} rounded-lg border transition-all ${
                        review.isPublished
                          ? "border-border/50 bg-background"
                          : "border-dashed border-muted-foreground/30 bg-muted/20 opacity-60"
                      }`}
                    >
                      <div className={isMobile ? "flex flex-col gap-3" : "flex items-start justify-between gap-3"}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <StarRating rating={review.rating} size="sm" />
                            <span className="text-xs font-medium text-foreground">
                              {getServiceName(review.serviceId)}
                            </span>
                            <span className="text-xs text-muted-foreground">·</span>
                            <span className="text-xs text-muted-foreground">
                              {getProfName(review.professionalId)}
                            </span>
                            <span className="text-xs text-muted-foreground">·</span>
                            <span className="text-xs text-muted-foreground">
                              {safeFormat(review.createdAt, "dd/MM/yyyy")}
                            </span>
                          </div>
                          {review.comment && (
                            <p className="text-sm text-foreground mt-2 leading-relaxed">
                              "{review.comment}"
                            </p>
                          )}
                          {review.adminReply ? (
                            <div className="mt-3 pl-3 border-l-2 border-primary/40">
                              <p className="text-xs font-medium text-primary mb-0.5">Resposta do salão:</p>
                              <p className="text-xs text-muted-foreground">{review.adminReply}</p>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground/60 mt-2 italic">Sem resposta ainda</p>
                          )}
                        </div>
                        <div className={`${isMobile ? "flex items-center justify-end gap-2 border-t border-border/60 pt-2" : "flex items-center gap-1.5 shrink-0"}`}>
                          <Badge
                            variant="outline"
                            className={review.isPublished ? "border-green-300 text-green-700" : "border-gray-300 text-gray-500"}
                          >
                            {review.isPublished ? "Publicada" : "Oculta"}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title={review.isPublished ? "Ocultar" : "Publicar"}
                            onClick={() =>
                              toggleMutation.mutate({ id: review.id, isPublished: !review.isPublished })
                            }
                            disabled={toggleMutation.isPending}
                          >
                            {review.isPublished ? (
                              <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                            ) : (
                              <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title={review.adminReply ? "Editar resposta" : "Responder"}
                            onClick={() => {
                              setReplyTarget({ id: review.id, currentReply: review.adminReply });
                              setReplyText(review.adminReply ?? "");
                            }}
                          >
                            <MessageSquare className={`h-3.5 w-3.5 ${review.adminReply ? "text-primary" : "text-muted-foreground"}`} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Avaliações pendentes */}
        <TabsContent value="pending">
          <Card className="border-border/50">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">Clientes Aguardando Avaliação</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Clientes que receberam a solicitação de avaliação mas ainda não responderam.
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Atualizar"
                onClick={() => refetchPending()}
              >
                <RefreshCw className="h-4 w-4 text-muted-foreground" />
              </Button>
            </CardHeader>
            <CardContent>
              {isPendingLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : !pendingSessions || pendingSessions.length === 0 ? (
                <div className="text-center py-10">
                  <Clock className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">Nenhuma avaliação pendente.</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    Todos os clientes que receberam solicitações já responderam.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingSessions.map((session) => {
                    const sentAt = session.reviewRequestSentAt ? new Date(session.reviewRequestSentAt) : null;
                    const reminderSent = !!session.reviewReminderSentAt;
                    const hoursAgo = sentAt
                      ? Math.floor((Date.now() - sentAt.getTime()) / (1000 * 60 * 60))
                      : null;

                    return (
                      <div
                        key={session.sessionId}
                        className="p-4 rounded-lg border border-border/50 bg-background flex items-center justify-between gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-foreground">
                              {session.clientName ?? "Cliente"}
                            </span>
                            {session.clientPhone && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                {session.clientPhone}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {sentAt && (
                              <span className="text-xs text-muted-foreground">
                                Solicitação enviada{" "}
                                {formatDistanceToNow(sentAt, { addSuffix: true, locale: ptBR })}
                              </span>
                            )}
                            {reminderSent ? (
                              <Badge variant="outline" className="text-xs border-orange-300 text-orange-600">
                                Lembrete enviado
                              </Badge>
                            ) : hoursAgo !== null && hoursAgo < 24 ? (
                              <Badge variant="outline" className="text-xs border-blue-300 text-blue-600">
                                Aguardando (lembrete em {24 - hoursAgo}h)
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs border-yellow-300 text-yellow-600">
                                Aguardando lembrete
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="shrink-0">
                          <Badge variant="outline" className="text-xs border-muted-foreground/30 text-muted-foreground">
                            Ag. #{session.pendingReviewAppointmentId}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reply dialog */}
      <Dialog open={!!replyTarget} onOpenChange={(v) => !v && setReplyTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Cormorant_Garamond,serif] text-xl">
              {replyTarget?.currentReply ? "Editar Resposta" : "Responder Avaliação"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">
              Sua resposta será exibida publicamente abaixo da avaliação do cliente.
            </p>
            <Textarea
              placeholder="Escreva uma resposta gentil e profissional..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={4}
              maxLength={1000}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">{replyText.length}/1000</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReplyTarget(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (!replyTarget) return;
                replyMutation.mutate({ id: replyTarget.id, adminReply: replyText.trim() });
              }}
              disabled={replyMutation.isPending || !replyText.trim()}
              className="bg-[#8B6914] hover:bg-[#7a5c12] text-white"
            >
              {replyMutation.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Enviando...</>
              ) : (
                "Enviar Resposta"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
